import React, { useCallback, useEffect, useState } from "react";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import { renderHourTick } from "../../../components/recharts/components";
import LegendSummaryTable, {
  ChartTable,
} from "../../../components/table/SummaryTable";
import { FilterTab } from "../../../components/Filters";
import Loading from "../../../components/Loading";
import { DataTabs } from "../../Hppc/Demand/Data";
import { BrushStart, ChartType } from "../../../models/chart_model";
import { getMonthlyPriceForecast } from "../../../Rest_api/RestApi";
import {
  ChartDataParams,
  FormatChartData,
} from "../../../components/recharts/functions";
import { IntervalFilter } from "./PriceForecastIEXWrapper";

export const monthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const IexMonthly = ({
  selectedTab,
  setSelectedTab,
  dataTab,
  setDataTab,
}: {
  selectedTab: any;
  setSelectedTab: any;
  dataTab: any;
  setDataTab: any;
}) => {
  const getChartSuffix = () => {
    const baseSuffix = [];

    if (selectedAvg) {
      baseSuffix.push({
        label: "",
        value: "actual_wt_avg_mcp",
      });
    } else {
      baseSuffix.push({
        label: "",
        value: "actual_mcp",
      });
    }

    return baseSuffix;
  };

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<any[]>([]);
  const [chartData, setChartData] = useState<any[]>([]);
  const [selectedAvg, setSelectedAvg] = useState<boolean>(true);
  const [chartKeys, setChartKeys] = useState<any[]>([]);
  const [tableData, setTableData] = useState<any[]>([]);

  const handleFetch = useCallback(async () => {
    setLoading(true);
    try {
      const rangeData = await getMonthlyPriceForecast();

      const transformedData = rangeData
        .map((item: any) => ({
          ...item,
          actual_mcp: item.avg_price_actual,
          actual_wt_avg_mcp: item.actual_wt_avg_price,
          // forecasted_mcp: item.forecasted_avg_price,
          month: `${monthNames[item.month - 1]} ${item.year}`,
          monthGroup: monthNames[item.month - 1],
        }))
        .sort(
          (a: { monthGroup: string }, b: { monthGroup: string }) =>
            monthNames.indexOf(a.monthGroup) - monthNames.indexOf(b.monthGroup)
        );

      setData(transformedData);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    handleFetch();
  }, [handleFetch]);

  useEffect(() => {
    let filteredData = data;
    const par: ChartDataParams = {
      keys: ["monthGroup"],
      tableKeys: ["monthGroup", "month", "year"],
      groupBy: ["year"],
      volumeKey: ``,
      aggregationKeys: selectedAvg ? ["actual_wt_avg_mcp"] : ["actual_mcp"],
      priceKey: "",
      data: filteredData,
    };
    const cData = FormatChartData(par);
    setChartData(cData.data);
    setChartKeys(cData.keys);
    setTableData(cData.tableData || []);
  }, [data, selectedAvg]);

  const createChartObject = (
    key: string,
    index: number,
    value: string,
    label: string
  ) => {
    const [barMonth, barYear] = key.split(" ");
    const now = new Date();
    const currentMonth = monthNames[now.getMonth()];
    const currentYear = now.getFullYear().toString();

    const isCurrentMonthYear =
      barMonth === currentMonth && barYear === currentYear;
    const isAug2025 = barMonth === "Aug" && barYear === "2025";

    return {
      dataKey: `${key}_${value}`,
      name: `${isCurrentMonthYear ? "Forecast " : ""}${key
        .replace("DAM", "")
        .replace("_", " ")
        .replace("undefined", "")} ${label}`,
      legendColor: undefined,
      type: ChartType.Bar,
      colorFormatter: (dataIndex: number, dataItem: any) => {
        return isAug2025 ? "#FF5733" : undefined; // Orange color for Aug 2025
      },
    };
  };

  function getLegends() {
    return getChartSuffix().flatMap(({ label, value }) =>
      chartKeys.map(
        (key, index) => createChartObject(key, index, value, label) as any
      )
    );
  }

  const tablePar = {
    legends: getLegends(),
    data: chartData,
    tableData: tableData,
    itemsPerPage: 96,
    unit: "Rs/KWh",
    extraKeys: ["monthGroup"],
    extraLabels: ["Month"],
    filename: "IEX_Monthly_Price_Forecast",
    showTotal: false,
  };

  const currentMonthIndex = new Date().getMonth(); // 0-based index
  const currentYear = new Date().getFullYear();
  const currentMonthName = monthNames[currentMonthIndex];

  return (
    <>
      <div className="gas-page-container p-4">
        {loading && <Loading />}

        <div style={{ margin: "0px" }} className="gas-chart-container">
          <div className="gas-heading gap-x-10">
            <h2 className="chartHeading w-60">DAM Price Forecast (IEX)</h2>
            <div className="flex gap-x-6  justify-end">
              <FilterTab
                options={IntervalFilter}
                selected={[selectedTab]}
                onClick={(option, index) => {
                  setSelectedTab(option);
                }}
              />
              {/* <FilterTab
                options={["Average", "Weighted Average"]}
                selected={[selectedAvg ? "Weighted Average" : "Average"]}
                onClick={(option, index) => {
                  setSelectedAvg(option === "Weighted Average");
                }}
              /> */}
              <FilterTab
                options={DataTabs.map((item) => item.label)}
                selected={[dataTab.label]}
                onClick={(option, index) => {
                  setDataTab(DataTabs[index]);
                }}
              />
            </div>
          </div>

          <div className="h-full w-full relative">
            {/* <span className="absolute z-10 top-2 left-1/2 transform -translate-x-1/2  text-gray-500 text-[0.8rem] bg-gray-100 px-2 py-1 rounded-lg">
              Monthly Weighted Average
            </span> */}
            {dataTab.label === "Chart" && (
              <>
                <ReMixChart
                  chartContainerHeight="calc(100vh - 250px)"
                  data={chartData}
                  showBrush={chartData.length > 96 ? true : undefined}
                  brushStart={BrushStart.Start}
                  isTimeSlot={false}
                  onlyTitle={true}
                  unit={"Rs/KWh"}
                  yAxisLabel={"Rs/KWh"}
                  secondYAxisLabel={"Rs/KWh"}
                  yMaxValue={10}
                  legends={getLegends() as any}
                  defaultSelectedLegends={getLegends().filter((legend: any) =>
                    legend.name.includes("2025")
                  )}
                  xDataKey={"monthGroup"}
                  xTick={(value: any, index: number): React.ReactNode => {
                    return renderHourTick(value, 10);
                  }}
                  isPriceForecast={true}
                  secondXDataKey={undefined}
                />
              </>
            )}
            {dataTab.label === "Summary" && (
              <div
                style={{ height: "calc(100vh - 200px)" }}
                className=" overflow-auto"
              >
                <LegendSummaryTable {...(tablePar as any)} />
              </div>
            )}
            {dataTab.label === "Table" && <ChartTable {...(tablePar as any)} />}
            <div className="flex justify-between items-center relative">
              <p
                style={{ fontSize: "0.85rem", color: "#666", marginTop: "8px" }}
              >
                <em>Note:</em> (1) MCP reflects a weighted average, (2) The
                values for{" "}
                <strong>
                  {currentMonthName} {currentYear}
                </strong>{" "}
                are forecasted estimates.
              </p>

              {/* {dataTab.label === "Chart" && (
                <div className="absolute bottom-11 left-12 flex gap-1">
                  {["Average", "Weighted Average"].map((label) => (
                    <button
                      key={label}
                      className={`px-2 py-0.5 rounded text-xs font-medium border transition
        ${
          selectedAvg === (label === "Weighted Average")
            ? "bg-[rgb(198 198 198)] text-primary border-[rgb(198 198 198)] rounded shadow-sm"
            : "bg-transparent text-gray-400 border-0"
        }`}
                      style={{ minWidth: 70 }}
                      onClick={() =>
                        setSelectedAvg(label === "Weighted Average")
                      }
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )} */}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default IexMonthly;
